import React, { ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { chain, useObjectRef } from '@react-aria/utils';
import { useControlledState } from '@react-stately/utils';
import { TextFieldBase } from '../../TextField/src/TextFieldBase';
import { TextFieldRef } from '../../TextField/src/TextFieldRef';
import { useSapphireTextField } from '../../TextField/src/useSapphireTextField';
import { useThemeCheck } from '../../utils';
import { TextFieldProps } from '../../TextField/src/TextField';
import { View, ViewOff } from '@danske/sapphire-icons/react';
import { Icon } from '../../Icon';
import { PasswordFieldButton } from './PasswordFieldButton';

export type PasswordFieldProps = Omit<TextFieldProps, 'prefix' | 'postfix'> & {
  /**
   * Shows or hides the password visibility toggle button.
   * @default false
   */
  showVisibilityToggle?: boolean;
};

export const PasswordField = forwardRef(function PasswordField(
  props: PasswordFieldProps,
  ref: ForwardedRef<TextFieldRef>
) {
  // the below is copied over from TextField
  useThemeCheck();
  const inputRef = useObjectRef<HTMLInputElement>(props.inputRef);
  const [showPassword, setShowPassword] = useState(false);
  const isDisabled = props.isDisabled;

  const [_, setInputValue] = useControlledState(
    props.value,
    props.defaultValue || '',
    () => {
      // do nothing
    }
  );
  const { labelProps, inputProps, errorMessageProps, descriptionProps } =
    useSapphireTextField(
      {
        ...props,
        onChange: chain(props.onChange, setInputValue),
        inputElementType: 'input',
      },
      inputRef
    );
  // the above is copied over from TextField

  useEffect(() => {
    if (isDisabled) {
      setShowPassword(false);
    }
  }, [isDisabled]);

  const enhancedInputProps = {
    ...inputProps,
    type: showPassword ? 'text' : 'password',
  };

  return (
    <TextFieldBase
      {...props}
      labelProps={labelProps}
      inputProps={enhancedInputProps}
      errorMessageProps={errorMessageProps}
      descriptionProps={descriptionProps}
      ref={ref}
      inputRef={inputRef}
      inputElementType="input"
      {...(props.showVisibilityToggle && {
        postfix: (
          <PasswordFieldButton
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            isDisabled={isDisabled}
            onClick={() => setShowPassword(!showPassword)}
          >
            <Icon size={props.size === 'md' ? 'sm' : 'md'}>
              {showPassword ? <View /> : <ViewOff />}
            </Icon>
          </PasswordFieldButton>
        ),
      })}
      alignInputRight={props.alignInputRight}
    />
  );
});
